import React, { useCallback, useState } from 'react';
import type { Placement } from '../components/ContentPreviewPopOver';
import PopOverArrow from '../components/PopOverArrow';
import PopOverPrimitive from '../components/PopOverPrimitive';
import contentPreviewPopOverSize from '../utils/contentPreviewPopOverSize';
import { LayoutSize } from '../utils/responsive/Sizes';
import useIsLayoutSize from '../utils/responsive/useIsLayoutSize';
import TopicDetailPreview from './TopicDetailPreview';
import styles from './TopicDetailPreviewPopOver.module.scss';

const SCREEN_PADDING = 12;

interface TopicDetailPreviewPopOverProps {
  topicId: string;
  children: React.ReactNode;
  placement?: Placement;
}

const TopicDetailPreviewPopOver = ({
  topicId,
  children,
  placement,
}: TopicDetailPreviewPopOverProps) => {
  const isMobile = useIsLayoutSize(LayoutSize.mobile);
  const [minHeight, setMinHeight] = useState<number | null>(null);

  const isHidden = minHeight === null;

  const getAdditionalMiddleware = useCallback<
    NonNullable<
      React.ComponentProps<typeof PopOverPrimitive>['getAdditionalMiddleware']
    >
  >(
    (options) => [
      contentPreviewPopOverSize({ ...options, fullWidth: isMobile }),
    ],
    [isMobile],
  );

  return (
    <PopOverPrimitive
      namespace="topicDetailPreview"
      screenPadding={SCREEN_PADDING}
      placement={placement}
      trigger="hover"
      enabledForTouch
      hideWhenReferenceHidden
      openDelayDuration={200}
      closeDelayDuration={100}
      getAdditionalMiddleware={getAdditionalMiddleware}
      noFlip
    >
      <PopOverPrimitive.Trigger asChild>{children}</PopOverPrimitive.Trigger>

      <PopOverPrimitive.Floating className={styles.floatingContainer}>
        <PopOverPrimitive.Content hidden={isHidden} className={styles.inner}>
          <TopicDetailPreview topicId={topicId} setMinHeight={setMinHeight} />
        </PopOverPrimitive.Content>
        {!isHidden && <PopOverArrow />}
      </PopOverPrimitive.Floating>
    </PopOverPrimitive>
  );
};

TopicDetailPreviewPopOver.Anchor = PopOverPrimitive.Anchor;

export default TopicDetailPreviewPopOver;
